import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaHome, FaTools, FaEnvelope, FaUserCog } from "react-icons/fa";
import { GiTechnoHeart } from "react-icons/gi";
import "./navbar.css";
import logo from "./LOGO_DOMOTIC_PRO.png";
import video1 from "./navbar_video_1.mp4";

const DomoticNavbar = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solucionesOpen, setSolucionesOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);
  const videoRef = useRef(null);
  const navbarRef = useRef(null);
  const navigate = useNavigate();

  // Lista de soluciones
  const soluciones = [
    { name: "AUTOMATIZACIÓN", path: "/Automatizacion" },
    { name: "SEGURIDAD ELECTRONICA Y CCTV", path: "/SecurityCctv" },
    { name: "NETWORKING Y REDES", path: "/RedesNetworking" },
    { name: "PLATAFORMAS PARA AUTOMATIZACIÓN DE PROCESOS", path: "/PLAutomatizacion" },
    { name: "ENERGÍA SOLAR", path: "/SolarSystems" },
    { name: "PUERTAS DE SEGURIDAD", path: "/Puertas-Seguridad" },
    { name: "PARQUEADEROS INTELIGENTES", path: "/IntelligentParking" },
    { name: "CÓMPUTO Y VIDEOCONFERENCIA", path: "/ComputoVideoConferencia" },
    { name: "PANTALLAS EDUCATIVAS", path: "/Pantallas-Señalización" },
    { name: "SISTEMAS POS", path: "/SistemasPOs" },
    { name: "LICENCIAMIENTO DE SOFTWARE", path: "/licenciamiento-software" },
  ];

  // Función para manejar clic en contacto
  const handleContactClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      const contactSection = document.getElementById("contacto");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, null, "#contacto");
      }
    } else {
      navigate("/#contacto");

      const waitForLoad = () => {
        const contactSection = document.getElementById("contacto");
        if (contactSection) {
          contactSection.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        } else {
          setTimeout(waitForLoad, 50);
        }
      };

      setTimeout(waitForLoad, 100);
    }
  };

  // Función para alternar el menú de soluciones
  const toggleSoluciones = () => {
    setSolucionesOpen(!solucionesOpen);
    if (partnersOpen) setPartnersOpen(false);
  };

  // Función para alternar el menú de partners
  const togglePartners = () => {
    setPartnersOpen(!partnersOpen);
    if (solucionesOpen) setSolucionesOpen(false);
  };

  // Cerrar menús al cambiar de página
  useEffect(() => {
    setSolucionesOpen(false);
    setPartnersOpen(false);
  }, [pathname]);

  // Efecto para manejar el scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Efecto para manejar el video
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const playVideo = () => {
        video.muted = true;
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn("Autoplay prevented:", error);
            document.addEventListener('click', () => video.play(), { once: true });
          });
        }
      };

      playVideo();
      video.addEventListener('loadedmetadata', playVideo);
      return () => video.removeEventListener('loadedmetadata', playVideo);
    }
  }, []);

  // Cerrar menú móvil al cambiar de página
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Manejar clic fuera del menú móvil
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (pathname === "/" && window.location.hash === "#domotic-contact") {
      const contactSection = document.getElementById("domotic-contact");
      if (contactSection) {
        setTimeout(() => {
          contactSection.scrollIntoView({ behavior: "smooth" });
          window.history.replaceState(null, null, ' ');
        }, 100);
      }
    }
  }, [pathname]);

  return (
    <div className="domotic-navbar-container" ref={navbarRef}>
      {/* Video de fondo - SIEMPRE VISIBLE */}
      <div className={`video-watermark ${scrolled ? "scrolled" : ""}`}>
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="watermark-video"
        >
          <source src={video1} type="video/mp4" />
          Tu navegador no soporta videos HTML5.
        </video>
      </div>

      {/* Navbar principal */}
      <nav className={`domotic-navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-inner">
          <Link to="/" className="navbar-brand">
            <img src={logo} alt="Logo Domotic" className="domotic-logo" />
          </Link>

          {/* Menú para desktop */}
          <ul className={`domotic-nav-links ${mobileMenuOpen ? "mobile-active" : ""}`}>
            <li>
              <Link
                to="/"
                className={`nav-link ${pathname === "/" ? "active" : ""}`}
              >
                <FaHome className="nav-icon" />
                <span>INICIO</span>
                <div className="nav-underline"></div>
              </Link>
            </li>

            <li>
              <Link
                to="/Nosotros"
                className={`nav-link ${pathname === "/Nosotros" ? "active" : ""}`}
              >
                <FaUserCog className="nav-icon" />
                <span>QUIÉNES SOMOS</span>
                <div className="nav-underline"></div>
              </Link>
            </li>

            {/* Menú desplegable Soluciones */}
            <li
              className="nav-item-with-menu"
              onClick={toggleSoluciones}
              style={{ cursor: 'pointer' }}
            >
              <div className="nav-link">
                <FaTools className="nav-icon" />
                <span>SOLUCIONES</span>
                <div className="nav-underline"></div>
              </div>

              {solucionesOpen && (
                <div className="hover-menu solutions-menu">
                  <ul className="hover-menu-list">
                    {soluciones.map((solucion, index) => (
                      <li key={index}>
                        <Link
                          to={solucion.path}
                          className="hover-menu-item"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setSolucionesOpen(false);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {solucion.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>

            {/* Menú desplegable Partners */}
            <li
              className="nav-item-with-menu"
              onClick={togglePartners}
              style={{ cursor: 'pointer' }}
            >
              <div className="nav-link">
                <GiTechnoHeart className="nav-icon" />
                <span>PARTNERS</span>
                <div className="nav-underline"></div>
              </div>

              {partnersOpen && (
                <div className="hover-menu partners-menu">
                  <div className="partners-content">
                    <p>Explora nuestras alianzas estratégicas con las mejores marcas del mercado.</p>
                    <Link
                      to="/Partners"
                      className="partners-link"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setPartnersOpen(false);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      Ver todos los partners
                    </Link>
                  </div>
                </div>
              )}
            </li>

            <li>
              <Link
                to="/#contacto"
                className={`nav-link ${pathname === "/" && window.location.hash === "#contacto" ? "active" : ""}`}
                onClick={handleContactClick}
              >
                <FaEnvelope className="nav-icon" />
                <span>CONTACTO</span>
                <div className="nav-underline"></div>
              </Link>
            </li>
          </ul>

          {/* Botón del menú móvil */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <span style={{ fontSize: '1.8rem' }}>✕</span>
            ) : (
              <span style={{ fontSize: '1.8rem' }}>☰</span>
            )}
          </button>
        </div>
      </nav>
    </div>
  );
};

export default DomoticNavbar;